import React, { useState, useEffect } from 'react';
import api, { BASE_URL, leadersAPI } from '../api';
import { toast } from 'react-toastify';
import './LeadersManager.css';

const LeadersManager = () => {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLeader, setEditingLeader] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    experience: '',
    description: '',
    social: { linkedin: '#', twitter: '#' },
    order: 0,
    isActive: true
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    fetchLeaders();
  }, []);

  const fetchLeaders = async () => {
    try {
      const response = await leadersAPI.getAllAdmin();
      setLeaders(response.data);
    } catch (error) {
      toast.error('Failed to fetch leaders');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (leader = null) => {
    if (leader) {
      setEditingLeader(leader);
      setFormData({
        name: leader.name,
        position: leader.position,
        experience: leader.experience,
        description: leader.description,
        social: leader.social || { linkedin: '#', twitter: '#' },
        order: leader.order || 0,
        isActive: leader.isActive
      });
      setImagePreview(leader.imageUrl ? `${BASE_URL}${leader.imageUrl}` : '');
    } else {
      setEditingLeader(null);
      setFormData({
        name: '',
        position: '',
        experience: '',
        description: '',
        social: { linkedin: '#', twitter: '#' },
        order: 0,
        isActive: true
      });
      setImageFile(null);
      setImagePreview('');
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingLeader(null);
    setImageFile(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'linkedin' || name === 'twitter') {
      setFormData({
        ...formData,
        social: { ...formData.social, [name]: value }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let leaderId;
      if (editingLeader) {
        await leadersAPI.update(editingLeader._id, formData);
        leaderId = editingLeader._id;
        toast.success('Leader updated successfully');
      } else {
        const response = await leadersAPI.create(formData);
        leaderId = response.data._id;
        toast.success('Leader created successfully');
      }
      
      if (imageFile) {
        await leadersAPI.uploadImage(leaderId, imageFile);
        toast.success('Image uploaded successfully');
      }
      
      handleCloseModal();
      fetchLeaders();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this leader?')) {
      try {
        await leadersAPI.delete(id);
        toast.success('Leader deleted successfully');
        fetchLeaders();
      } catch (error) {
        toast.error('Failed to delete leader');
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading leadership team...</p>
      </div>
    );
  }

  return (
    <div className="leaders-manager">
      <div className="page-header">
        <div className="header-content">
          <div className="header-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div>
            <h2>Leadership Team Management</h2>
            <p className="header-subtitle">Manage your organization's leadership profiles</p>
          </div>
        </div>
        <button className="btn-add" onClick={() => handleOpenModal()}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add New Leader
        </button>
      </div>

      <div className="table-wrapper">
        <table className="leaders-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Name</th>
              <th>Position</th>
              <th>Experience</th>
              <th>Description</th>
              <th>Status</th>
              <th>Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {leaders.map((leader) => (
              <tr key={leader._id}>
                <td className="image-cell">
                  {leader.imageUrl ? (
                    <div className="leader-avatar">
                      <img 
                        src={`${BASE_URL}${leader.imageUrl}`} 
                        alt={leader.name}
                      />
                    </div>
                  ) : (
                    <div className="avatar-placeholder">
                      {leader.name.charAt(0)}
                    </div>
                  )}
                </td>
                <td className="name-cell">{leader.name}</td>
                <td className="position-cell">{leader.position}</td>
                <td className="experience-cell">{leader.experience}</td>
                <td className="description-cell">
                  <div className="description-preview">
                    {leader.description.length > 60 
                      ? `${leader.description.substring(0, 60)}...` 
                      : leader.description}
                  </div>
                </td>
                <td>
                  <span className={`status-badge ${leader.isActive ? 'status-active' : 'status-inactive'}`}>
                    <span className="status-dot"></span>
                    {leader.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="order-cell">
                  <span className="order-badge">{leader.order}</span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn edit-btn" onClick={() => handleOpenModal(leader)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34" />
                        <polygon points="18 2 22 6 12 16 8 16 8 12 18 2" />
                      </svg>
                      Edit
                    </button>
                    <button className="action-btn delete-btn" onClick={() => handleDelete(leader._id)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {leaders.length === 0 && (
          <div className="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <h3>No leaders found</h3>
            <p>Click the "Add New Leader" button to create your first leadership profile.</p>
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <h2>{editingLeader ? 'Edit Leader Profile' : 'Add New Leader'}</h2>
              </div>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input 
                    type="text" 
                    name="name" 
                    value={formData.name} 
                    onChange={handleInputChange} 
                    required 
                    placeholder="e.g., John Doe"
                  />
                </div>
                
                <div className="form-group">
                  <label>Position *</label>
                  <input 
                    type="text" 
                    name="position" 
                    value={formData.position} 
                    onChange={handleInputChange} 
                    required 
                    placeholder="e.g., CEO"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Experience *</label>
                <input 
                  type="text" 
                  name="experience" 
                  value={formData.experience} 
                  onChange={handleInputChange} 
                  required 
                  placeholder="e.g., 15+ years in technology leadership"
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea 
                  name="description" 
                  value={formData.description} 
                  onChange={handleInputChange} 
                  rows="5" 
                  required 
                  placeholder="Provide a detailed description of the leader's background, achievements, and role..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>LinkedIn URL</label>
                  <div className="input-with-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                      <rect x="2" y="9" width="4" height="12" />
                      <circle cx="4" cy="4" r="2" />
                    </svg>
                    <input 
                      type="url" 
                      name="linkedin" 
                      value={formData.social.linkedin} 
                      onChange={handleInputChange} 
                      placeholder="https://linkedin.com/in/username"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Twitter URL</label>
                  <div className="input-with-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                    </svg>
                    <input 
                      type="url" 
                      name="twitter" 
                      value={formData.social.twitter} 
                      onChange={handleInputChange} 
                      placeholder="https://twitter.com/username"
                    />
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Display Order</label>
                  <input 
                    type="number" 
                    name="order" 
                    value={formData.order} 
                    onChange={handleInputChange} 
                    placeholder="0"
                  />
                  <small>Lower numbers appear first</small>
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      name="isActive" 
                      checked={formData.isActive} 
                      onChange={handleInputChange} 
                    />
                    <span>Active Status</span>
                  </label>
                  <small>Inactive leaders won't appear on the website</small>
                </div>
              </div>

              <div className="form-group">
                <label>Profile Image</label>
                <div className="image-upload-container">
                  <div className="image-preview">
                    {imagePreview ? (
                      <div className="preview-wrapper">
                        <img src={imagePreview} alt="Preview" />
                        <button 
                          type="button" 
                          className="remove-image"
                          onClick={() => {
                            setImageFile(null);
                            setImagePreview('');
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="preview-placeholder">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21 15 16 10 5 21" />
                        </svg>
                        <p>No image selected</p>
                      </div>
                    )}
                  </div>
                  <div className="upload-controls">
                    <label className="upload-btn">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M12 3v12m0 0-3-3m3 3 3-3M5 21h14" />
                      </svg>
                      Choose Image
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleImageChange} 
                        style={{ display: 'none' }}
                      />
                    </label>
                    <small>Recommended: Square image, min 300x300px</small>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                    <polyline points="17 21 17 13 7 13 7 21" />
                    <polyline points="7 3 7 8 15 8" />
                  </svg>
                  {editingLeader ? 'Update Leader' : 'Create Leader'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadersManager;